import React from 'react';
import { Link } from 'react-router-dom';
import { Target, TrendingUp, Users, Zap, BarChart3, Globe, ArrowRight, CheckCircle } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { ServiceSchema } from '../../components/SchemaMarkup';
import { useBanner } from '../../contexts/BannerContext';
import { FAQ } from '../../components/FAQ';

export const CognitiveReachPage: React.FC = () => {
  useSEO('cognitive-reach');
  const { isBannerVisible, bannerHeight } = useBanner();

  const features = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "AI-Powered Analytics",
      description: "Advanced machine learning algorithms analyze customer behavior and market trends to optimize your marketing strategies."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Audience Segmentation",
      description: "Intelligent customer segmentation based on behavior patterns, preferences, and engagement metrics."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Automated Campaigns",
      description: "Smart automation tools that create, optimize, and manage marketing campaigns across multiple channels."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Performance Tracking",
      description: "Real-time performance monitoring with detailed insights and actionable recommendations."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Multi-Channel Integration",
      description: "Seamless integration across social media, email, web, and mobile platforms for unified campaigns."
    }
  ];

  const benefits = [
    "Increase conversion rates significantly",
    "Reduce marketing costs through intelligent automation",
    "Improve customer engagement and retention",
    "Generate actionable insights from data",
    "Scale marketing efforts efficiently",
    "Personalize customer experiences at scale"
  ];

  const processes = [
    {
      step: 1,
      title: "Discovery & Analysis",
      description: "We analyze your current marketing performance, target audience, and business goals to create a comprehensive strategy."
    },
    {
      step: 2,
      title: "AI Implementation",
      description: "Deploy our advanced AI tools to automate campaigns, analyze customer data, and optimize marketing funnels."
    },
    {
      step: 3,
      title: "Campaign Optimization",
      description: "Continuously monitor and optimize campaigns using machine learning insights for maximum ROI."
    },
    {
      step: 4,
      title: "Scaling & Growth",
      description: "Scale successful strategies across multiple channels while maintaining performance and efficiency."
    }
  ];

  const faqItems = [
    {
      question: "How quickly can I see results from AI-powered marketing campaigns?",
      answer: "Most clients see initial improvements within 2-4 weeks of implementation. Significant ROI improvements typically occur within 60-90 days as our AI algorithms learn and optimize your campaigns."
    },
    {
      question: "Do I need technical expertise to use Cognitive Reach?",
      answer: "No technical expertise required. Our platform is designed for marketers and business owners. We provide comprehensive training and ongoing support to ensure successful adoption."
    },
    {
      question: "Can Cognitive Reach integrate with my existing marketing tools?",
      answer: "Yes, we support integration with major marketing platforms including Google Ads, Facebook Ads, email marketing tools, CRM systems, and analytics platforms. Our team handles the technical setup."
    },
    {
      question: "What type of businesses benefit most from AI marketing automation?",
      answer: "Businesses with digital marketing budgets over $5,000/month see the greatest impact. This includes e-commerce, SaaS companies, professional services, and B2B organizations looking to scale their marketing efforts."
    },
    {
      question: "How does pricing work for Cognitive Reach services?",
      answer: "We offer flexible pricing based on your marketing budget and needs. Our packages start at $2,500/month and include strategy development, AI implementation, campaign management, and ongoing optimization."
    },
    {
      question: "What kind of support do you provide during implementation?",
      answer: "We provide dedicated account management, weekly optimization reviews, real-time performance monitoring, and 24/7 technical support. Our team becomes an extension of your marketing department."
    }
  ];


  return (
    <>
      <ServiceSchema
        serviceName="Cognitive Reach"
        description="AI-powered digital marketing solutions that transform your marketing strategy with intelligent automation, data-driven insights, and campaign optimization."
        serviceType="Digital Marketing"
        url="https://cognitivecrafthub.com/services/cognitive-reach"
        features={[
          "AI-Powered Analytics",
          "Audience Segmentation", 
          "Automated Campaigns",
          "Performance Tracking",
          "Multi-Channel Integration"
        ]}
      />
      <Breadcrumb />
      <main className="pb-20">
        {/* Hero Section */}
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl mb-6">
              <Target className="w-10 h-10 text-white" />
            </div>
            <h1 
              className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
              style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
            >
              Cognitive Reach
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              AI-Powered Digital Marketing Solutions
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Transform your marketing strategy with intelligent automation, data-driven insights, 
              and AI-powered campaign optimization that amplifies your brand's reach across all digital channels.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Powerful Marketing Features</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Our AI-powered marketing platform combines cutting-edge technology with proven strategies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
                <div className="text-blue-600 dark:text-blue-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Why Choose Cognitive Reach?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Our Process</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              A proven methodology that delivers measurable results
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {processes.map((process, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{process.title}</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <div id="faq">
        <FAQ 
          items={faqItems}
          serviceName="Cognitive Reach"
        />
      </div>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Amplify Your Marketing Reach?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              Let's discuss how Cognitive Reach can transform your digital marketing strategy 
              and drive measurable growth for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                Get Started Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};